import React, { useEffect, useState, Suspense, lazy } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ScrollButton from "./components/ScrollButton";
import Preloader from "./components/Preloader";
import FooterStrip from "./pages/Home/modules/FooterStrip";
import MobileNavigator from "./components/MobileNavigator";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import api from "./api";

// Lazy load all route components for code splitting
const Home = lazy(() => import("./pages/Home/Home"));
const ProductDetails = lazy(() => import("./pages/Products/ProductDetails"));
const ProductCategories = lazy(() => import("./pages/Products/ProductCategories"));
const ShopProduct = lazy(() => import("./pages/Products/ShopProduct"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const WhyUs = lazy(() => import("./pages/WhyUs"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Faqs = lazy(() => import("./pages/Faqs"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Blogs = lazy(() => import("./pages/Blogs/Blogs"));
const BlogDetails = lazy(() => import("./pages/Blogs/BlogDetails"));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const PurchaseHistory = lazy(() => import("./pages/Dashboard/PurchaseHistory"));
const Wishlists = lazy(() => import("./pages/Dashboard/Wishlists"));
const Profile = lazy(() => import("./pages/Dashboard/Profile"));
const OrderDetails = lazy(() => import("./pages/Dashboard/OrderDetails"));
const WarrantyPolicy = lazy(() => import("./pages/Policy/WarrantyPolicy"));
const ShippingPolicy = lazy(() => import("./pages/Policy/ShippingPolicy"));
const ReturnPolicy = lazy(() => import("./pages/Policy/ReturnPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/Policy/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/Policy/TermsConditions"));
const Stories = lazy(() => import("./pages/Stories/Stories"));
const Cart = lazy(() => import("./components/Cart"));
const Checkout = lazy(() => import("./components/Checkout"));
const Login = lazy(() => import("./components/Login"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const Registration = lazy(() => import("./components/Registration"));
const PaymentResponse = lazy(() => import("./components/PaymentResponse"));
const PaymentResponse1 = lazy(() => import("./components/PaymentResponse1"));
const DefaultPage = lazy(() => import("./pages/DefaultPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));

function App() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginData, setLoginData] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [wishlistItem, setWishlistItem] = useState([]);
  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState([]);
  const [cartSummary, setcartSummary] = useState([]);
  const phoneSetting = settings.find(item => item.type === "contact_phone");
  const contactPhone = phoneSetting ? phoneSetting.value : "";
  const [notification, setNotification] = useState({ message: "", type: "" });

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 1000);
  };

  const fetchCartData = async () => {
    const token = sessionStorage.getItem("access_token");
    const isLoggedIn = !!loginData?.id;
    const userId = isLoggedIn ? loginData.id : getGuestId();

    if (!userId) return;

    try {
      // Build params dynamically
      const params = new URLSearchParams(
        isLoggedIn ? { user_id: userId } : { temp_user_id: userId }
      ).toString();

      const response = await api.post(`/carts?${params}`, null, {
        headers: isLoggedIn && token ? { Authorization: `Bearer ${token}` } : {}
      });

      const cartItems = response.data.data[0]?.cart_items || [];
      setGrandTotal(response.data.grand_total || 0);
      setCartItems(cartItems);
      setProducts(cartItems);
      setQuantities(cartItems.map(item => item.quantity || 1));
      setError(null);
    } catch {
      setError("Failed to fetch cart data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCartSummary = async () => {
    const token = sessionStorage.getItem("access_token");
    const isLoggedIn = !!loginData?.id;
    const userId = isLoggedIn ? loginData.id : getGuestId();

    if (!userId) return;

    try {
      // Build params dynamically
      const params = new URLSearchParams(
        isLoggedIn ? { user_id: userId } : { temp_user_id: userId }
      ).toString();

      const response = await api.post(`/cart-summary?${params}`, null, {
        headers: isLoggedIn && token ? { Authorization: `Bearer ${token}` } : {}
      });

      setcartSummary(response.data);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load cart data");
    } finally {
      setLoading(false);
    }
  };


  // Guest ID utilities
  const getGuestId = () => {
    let guestId = getCookie("guest_id");
    if (!guestId) {
      guestId = generateGuestId();
      setCookie("guest_id", guestId, 30);
    }
    return guestId;
  };

  const generateGuestId = () =>
    "guest_" + Math.random().toString(36).substr(2, 9);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const setCookie = (name, value, days) => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value}; ${expires}; path=/`;
  };

  // Cart Item removal
  const removeItemFromCart = async (cartItemId) => {
    const token = sessionStorage.getItem("access_token");
    const isLoggedIn = !!loginData?.id;
    const userId = isLoggedIn ? loginData.id : getGuestId();

    if (!userId) {
      showNotification("Unable to identify user.", "error");
      return;
    }

    try {
      const response = await api.delete(`/carts/${cartItemId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.result) {
        setProducts((prev) => prev.filter((item) => item.id !== cartItemId));
        setQuantities((prev) =>
          prev.filter((_, idx) => products[idx]?.id !== cartItemId)
        );

        await fetchCartData();
        await fetchCartSummary();

        showNotification(response.data.message, "success");
      } else {
        showNotification(response.data.message || "Failed to remove item.", "error");
      }
    } catch (error) {
      showNotification("Error removing item from cart", "error");
    }
  };



  // Quantity change
  const handleQuantityChange = async (index, increment) => {
    const newQuantities = [...quantities];
    newQuantities[index] = Math.max(1, newQuantities[index] + increment);

    try {
      const token = sessionStorage.getItem("access_token");
      const response = await api.post(
        "/carts/change-quantity",
        {
          id: products[index].id,
          quantity: newQuantities[index],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.result) {
        await fetchCartData();
        await fetchCartSummary();
        showNotification(response.data.message, "success");
      } else {
        showNotification(response.data.message, "error");
      }
    } catch {
      showNotification("Error updating cart", "error");
    }
  };

  // Add to cart
  const handleCart = async (id, variant, user_id, quantity) => {
    const token = sessionStorage.getItem("access_token");
    const guestId = getGuestId();
    const isLoggedIn = !!loginData?.id;

    try {
      const response = await api.post(
        "/carts/add",
        {
          id,
          variant,
          [isLoggedIn ? "user_id" : "temp_user_id"]: isLoggedIn ? user_id : guestId,
          quantity,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (response.data?.result) {
        showNotification(response.data.message, "success");
        await fetchCartData();
        await fetchCartSummary();
      } else {
        showNotification(response.data.message, "error");
      }
    } catch {
      showNotification('An error occurred while processing your request.', "error");
    }
  };


  // Wishlist actions
  const handleWishlist = async (slug) => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      showNotification('You need to be logged in to add items to the wishlist.', "error");
      return;
    }

    try {
      const response = await api.get(`/wishlists-add-product/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.result) {
        fetchWishlistCount();
        showNotification(response.data.message, "success");
      } else {
        showNotification(response.data.message, "error");
      }
    } catch {
      showNotification('An error occurred while processing your request.', "error");
    }
  };

  const handleWishlistRemove = async (slug) => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      showNotification('You need to be logged in to remove items from the wishlist.', "error");
      return;
    }

    try {
      const response = await api.get(`/wishlists-remove-product/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.is_in_wishlist === false) {
        showNotification(response.data.message, "success");
        setWishlistItem((prev) =>
          prev.filter((item) => item.product.slug !== slug)
        );
        setWishlistCount(wishlistCount - 1);
      } else {
        showNotification(response.data.message, "error");
      }
    } catch {
      showNotification('An error occurred. Please try again.', "error");
    }
  };

  const fetchWishlistCount = async () => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      setWishlistCount(0);
      return;
    }
    try {
      const response = await api.get("/wishlists", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWishlistItem(response.data.data);
      setWishlistCount(response.data.data.length);
    } catch {
      setWishlistCount(0);
    }
  };

  // Effects
  useEffect(() => {
    fetchCartData();
    fetchCartSummary();
  }, [loginData]);

  useEffect(() => {
    fetchWishlistCount();
  }, []);

  useEffect(() => {
    const userData = sessionStorage.getItem("user_data");
    if (userData) {
      try {
        setLoginData(JSON.parse(userData));
      } catch {
        setLoginData(null);
      }
    } else {
      setLoginData(null);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/business-settings");
        setSettings(response.data.data.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (window.console && console.log) {
      const originalLog = console.log;
      console.log = function (...args) {
        if (args[0] && typeof args[0] === 'string' && args[0].includes('header_tags')) {
          return;
        }
        if (args[0] && args[0].userfrom === 'tagembed') {
          return;
        }
        originalLog.apply(console, args);
      };
    }
  }, []);

  return (
    <Router>
      {notification.message && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      <ScrollToTop />
      <div className="App">
        {loading && <Preloader />}
        <Header cartItems={cartItems} grandTotal={grandTotal} removeItemFromCart={removeItemFromCart} wishlistCount={wishlistCount} cartSummary={cartSummary} />
        <MobileNavigator cartItems={cartItems} />
        <Suspense fallback={<Preloader />}>
          <Routes>
            <Route path="/" element={
              <Home cartItems={cartItems} quantities={quantities} handleCart={handleCart} handleWishlist={handleWishlist} handleQuantityChange={handleQuantityChange} removeItemFromCart={removeItemFromCart}/>
            }
            />
            <Route
              path="/products/:slug"
              element={
                <ProductDetails
                 cartItems={cartItems} quantities={quantities} handleCart={handleCart} handleWishlist={handleWishlist} handleQuantityChange={handleQuantityChange} removeItemFromCart={removeItemFromCart}
                />
              }
            />
            <Route
              path="/shop"
              element={
                <ShopProduct
                 cartItems={cartItems} quantities={quantities} handleCart={handleCart} handleWishlist={handleWishlist} handleQuantityChange={handleQuantityChange} removeItemFromCart={removeItemFromCart}
                />
              }
            />
            <Route
              path="/category/:slug"
              element={
                <ProductCategories
                  cartItems={cartItems}
                  quantities={quantities}
                  handleQuantityChange={handleQuantityChange}
                  handleCart={handleCart}
                  handleWishlist={handleWishlist}
                  removeItemFromCart={removeItemFromCart}
                />
              }
            />
            <Route path="/about-us" element={<AboutUs setting={settings} />} />
            <Route
              path="/contact-us"
              element={<ContactUs setting={settings} />}
            />
            <Route path="/why-us" element={<WhyUs setting={settings} />} />
            <Route path="/sitemap" element={<Sitemap setting={settings} />} />
            <Route path="/faqs" element={<Faqs setting={settings} />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blog/:slug" element={<BlogDetails />} />
            <Route path="/stories" element={<Stories />} />
            <Route
              path="/cart"
              element={
                <Cart
                  products={cartItems}
                  quantities={quantities}
                  handleQuantityChange={handleQuantityChange}
                  removeItemFromCart={removeItemFromCart}
                  cartSummary={cartSummary}
                />
              }
            />

            <Route path="/checkout" element={<Checkout products={cartItems} cartSummary={cartSummary} />} />


            <Route path="/payment-response" element={<PaymentResponse />} />
            <Route path="/payment-response1" element={<PaymentResponse1 />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/dashboard"
              element={
                loginData ? <Dashboard /> : <Navigate to="/login" replace />
              }
            />
            <Route path="/purchase-history" element={<PurchaseHistory />} />
            <Route path="/order-details/:id" element={<OrderDetails />} />
            <Route
              path="/wishlist"
              element={
                <Wishlists
                  handleCart={handleCart}
                  wishlistItem={wishlistItem}
                  handleWishlistRemove={handleWishlistRemove}
                />
              }
            />
            <Route path="/search" element={<SearchPage handleCart={handleCart} handleWishlist={handleWishlist} />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/warranty-policy" element={<WarrantyPolicy />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/return-policy" element={<ReturnPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="*" element={<DefaultPage />} />
          </Routes>
        </Suspense>
        <FooterStrip />
        <Footer setting={settings} />

        <ScrollButton />
        <a
          href={`https://wa.me/${contactPhone}`}
          className="whatsapp-float"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="https://cdn-icons-png.flaticon.com/512/733/733585.png"
            alt="WhatsApp"
            style={{ width: "50px", height: "50px" }}
            loading="lazy"
          />
        </a>

      </div>
    </Router>
  );
}

export default App;
